import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import { createDebugConfig, trackCustomEvent } from '@/configuration/setup/googleAnalytics';

interface CustomExpanderPanelProps {
    header: ReactElement;
    body: ReactElement;
    bodyDetails?: ReactElement;
    isDisabled?: boolean;
}

const CustomExpanderPanel = ({ header, body, bodyDetails, isDisabled = false }: CustomExpanderPanelProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState('0px');

    const updateHeight = useCallback(() => {
        setHeight(isExpanded && contentRef.current ? `${contentRef.current.scrollHeight}px` : '0px');
    }, [isExpanded]);

    // MutationObserver to detect changes in DOM
    useEffect(() => {
        if (!isExpanded || !contentRef.current) {
            setHeight('0px');
            return;
        }

        updateHeight();

        const mutationObserver = new MutationObserver(() => setTimeout(updateHeight, 10));
        mutationObserver.observe(contentRef.current, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class'],
        });

        const resizeObserver = new ResizeObserver(() => setTimeout(updateHeight, 10));
        resizeObserver.observe(contentRef.current);

        return () => {
            mutationObserver.disconnect();
            resizeObserver.disconnect();
        };
    }, [isExpanded, updateHeight, bodyDetails]);

    const handleOpenExpander = () => {
        setIsExpanded(prev => {
            if (!prev) {
                trackCustomEvent(
                    'open_order_details',
                    { trigger: 'click', flow_name: 'card_ordering_history' },
                    createDebugConfig()
                );
            }
            return !prev;
        });
    };

    return (
        <div className={`panel panel-default expander-panel ${isDisabled ? 'disabled' : ''}`}>
            <div className={`panel-heading ${isExpanded && 'open'}`} onClick={handleOpenExpander}>
                <div className='display-flex align-items-center'>{header}</div>
                <span className='uikit-icon icon-chevron-down expander-icon' />
            </div>
            <div className='collapse display-block width-100pct'>
                <div
                    className='panel-body rounded-bottom padding-y-20'
                    style={{
                        backgroundColor: `rgba(246, 248, 249, ${isExpanded ? 1 : 0})`,
                        transition: 'background-color 0.3s ease-in-out',
                    }}
                >
                    {body}
                    <div
                        ref={contentRef}
                        style={{
                            height,
                            overflow: 'hidden',
                            transition: 'height 0.3s ease-in-out',
                        }}
                    >
                        {isExpanded && bodyDetails}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomExpanderPanel;
